/**
 * Date utilities for Colombia timezone (America/Bogota, UTC-5).
 * Always use these helpers instead of new Date().toISOString() to avoid
 * UTC offset issues that shift the date by one day.
 */

const TZ = 'America/Bogota';

/**
 * Returns today's date string in YYYY-MM-DD format, adjusted to Colombia time.
 */
export function getTodayCO(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: TZ }); // en-CA gives YYYY-MM-DD
}

/**
 * Returns a Date object representing "now" in Colombia time.
 * Useful for getting correct hours/minutes for the current time indicator.
 */
export function getNowCO(): { hours: number; minutes: number; dateStr: string } {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0';

  const hours = parseInt(get('hour'), 10);
  const minutes = parseInt(get('minute'), 10);
  const year = get('year');
  const month = get('month');
  const day = get('day');
  const dateStr = `${year}-${month}-${day}`;

  return { hours, minutes, dateStr };
}

/**
 * Returns a Date object set to today in Colombia time (midnight local).
 * Safe to use for calendar navigation.
 */
export function getTodayDateCO(): Date {
  const ds = getTodayCO();
  const [y, m, d] = ds.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Converts a Date object to YYYY-MM-DD string using Colombia timezone.
 */
export function toDateStrCO(date: Date): string {
  return date.toLocaleDateString('en-CA', { timeZone: TZ });
}

/**
 * Returns a date N days before today in Colombia time, as YYYY-MM-DD.
 */
export function daysAgoCO(days: number): string {
  const d = getTodayDateCO();
  d.setDate(d.getDate() - (days - 1));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
